// Mises à jour de l'application installée.
//
// Une PWA installée ne se met pas à jour comme un site : le service worker
// resert sa copie du code tant qu'il n'a pas été remplacé. Sans rien faire, un
// joueur peut rester des semaines sur une version périmée sans le savoir — il
// n'y a aucune boutique pour le lui dire.
//
// ── Ce que fait ce module ───────────────────────────────────────────────────
//
// Il demande au navigateur, régulièrement, s'il existe une nouvelle version ;
// et quand il y en a une, il la laisse ATTENDRE au lieu de l'appliquer. C'est
// le joueur qui bascule, d'un bouton.
//
// L'application ne se recharge donc jamais sous les doigts de quelqu'un. Le
// cas qu'on veut éviter est précis : une bascule automatique tombant entre deux
// balles d'une même série, l'écran qui se recharge, et un joueur qui ne sait
// plus si sa saisie est partie.
//
// ── Quand la question est posée ─────────────────────────────────────────────
//
//   * au démarrage — le navigateur interroge le service worker à
//     l'enregistrement, sans qu'on ait à le demander ;
//   * au retour sur l'application, si la dernière question date de plus d'une
//     minute. C'est le moment utile : on sort son téléphone pour s'en servir,
//     autant lui donner la version du jour ;
//   * toutes les heures tant que l'écran reste ouvert — une séance de practice
//     dure plus longtemps qu'on ne croit ;
//   * et à la demande, depuis les préférences.
//
// La garde d'une minute évite qu'un aller-retour entre deux écrans ne déclenche
// une requête réseau à chaque fois.

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';

/** Résultat d'une recherche explicite, pour l'écran de préférences. */
export type UpdateCheck =
    | { state: 'idle' }
    | { state: 'checking' }
    | { state: 'found' }
    | { state: 'up-to-date' }
    | { state: 'unsupported' };

interface UpdatesValue {
    /** Une version est prête et attend le feu vert. */
    ready: boolean;
    /** Bascule sur la nouvelle version et recharge. */
    apply: () => void;
    /** Interroge le serveur maintenant. */
    check: () => Promise<void>;
    lastCheck: UpdateCheck;
    /** Version embarquée dans le build en cours d'exécution. */
    version: string;
}

const Ctx = createContext<UpdatesValue>({
    ready: false,
    apply: () => {},
    check: async () => {},
    lastCheck: { state: 'idle' },
    version: 'dev',
});

const HOUR_MS = 3_600_000;
const MIN_GAP_MS = 60_000;

export function UpdatesProvider({ children }: { children: React.ReactNode }) {
    const [lastCheck, setLastCheck] = useState<UpdateCheck>({ state: 'idle' });
    const registration = useRef<ServiceWorkerRegistration | null>(null);
    const lastAt = useRef(0);

    const {
        needRefresh: [needRefresh],
        updateServiceWorker,
    } = useRegisterSW({
        onRegisteredSW(_url, r) {
            registration.current = r ?? null;
        },
        onRegisterError(err) {
            console.error('[pwa] enregistrement impossible :', err);
        },
    });

    const check = useCallback(async (): Promise<void> => {
        const r = registration.current;
        if (!r) {
            // Pas de service worker : navigation privée, contexte non
            // sécurisé, ou simplement le mode développement où il est
            // volontairement désactivé. Le dire plutôt que faire semblant.
            setLastCheck({ state: 'unsupported' });
            return;
        }
        setLastCheck({ state: 'checking' });
        lastAt.current = Date.now();
        try {
            await r.update();
            // `update()` ne dit pas ce qu'il a trouvé : c'est l'apparition d'un
            // worker en attente qui le dit. On laisse au navigateur le temps de
            // le mettre en place avant de conclure.
            await new Promise((res) => setTimeout(res, 800));
            setLastCheck(r.waiting || r.installing ? { state: 'found' } : { state: 'up-to-date' });
        } catch {
            // Hors ligne, ou serveur injoignable : on ne sait pas, et prétendre
            // « à jour » serait faux.
            setLastCheck({ state: 'idle' });
        }
    }, []);

    // Rythme automatique : à l'heure, et au retour sur l'application.
    useEffect(() => {
        const silent = (): void => {
            if (Date.now() - lastAt.current < MIN_GAP_MS) return;
            lastAt.current = Date.now();
            void registration.current?.update().catch(() => { /* réessai plus tard */ });
        };

        const timer = window.setInterval(silent, HOUR_MS);
        const onVisible = (): void => {
            if (document.visibilityState === 'visible') silent();
        };
        document.addEventListener('visibilitychange', onVisible);
        window.addEventListener('focus', onVisible);

        return () => {
            window.clearInterval(timer);
            document.removeEventListener('visibilitychange', onVisible);
            window.removeEventListener('focus', onVisible);
        };
    }, []);

    /* La bascule se fait TOUTE SEULE.
     *
     *  Elle demandait un geste, et ce geste ne venait pas : on ne lit pas une
     *  bande d'information quand on est venu taper des balles. Résultat, des
     *  appareils tournaient des jours sur une version périmée, et les
     *  corrections livrées n'atteignaient personne.
     *
     *  Une exception, une seule : PENDANT LA SAISIE d'un exercice. Un
     *  rechargement sous le pouce, entre deux balles, ferait manquer une
     *  frappe — c'est le seul moment où le coût dépasse le bénéfice. L'état
     *  est pourtant intégralement côté serveur et un rechargement le
     *  restituerait ; ce qu'on protège ici, c'est le geste en cours, pas la
     *  donnée.
     *
     *  On revérifie donc régulièrement plutôt que d'abandonner : dès que le
     *  joueur quitte l'écran de saisie, la mise à jour s'applique. La bande
     *  reste, pour qui veut basculer sans attendre.
     */
    useEffect(() => {
        if (!needRefresh) return undefined;

        const enSaisie = (): boolean =>
            window.location.pathname.startsWith('/entrainement/exercices');

        const appliquer = (): void => {
            if (enSaisie()) return;
            void updateServiceWorker(true);
        };

        appliquer();
        const timer = window.setInterval(appliquer, 20_000);
        return () => window.clearInterval(timer);
    }, [needRefresh, updateServiceWorker]);

    const value = useMemo<UpdatesValue>(() => ({
        ready: needRefresh,
        // `true` : le worker en attente prend la main immédiatement et la page
        // se recharge. Emprunté par la bascule automatique comme par la bande,
        // qui ne se distinguent que par le MOMENT choisi.
        apply: () => void updateServiceWorker(true),
        check,
        lastCheck,
        version: (import.meta.env.VITE_APP_VERSION as string | undefined) ?? 'dev',
    }), [needRefresh, updateServiceWorker, check, lastCheck]);

    return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useUpdates = (): UpdatesValue => useContext(Ctx);
